import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import ICategory from '../../../../../model/ICategory';
import ComplexAccordion from '../../../../../../gqlCommon/components/presentation/genericAccordion/ComplexAccordion';
import FeatureCategoryTitle from '../featureCategoryTitle/FeatureCategoryTitle';
import FeatureCategoryLayoutDisplay from '../FeatureCategoryLayoutDisplay';
import featureCategoryStyles from './featureCategoryStyles';
import availableFeaturesService from '../../../../../service/availableFeaturesService';
import IFeature from '../../../../../model/IFeature';
import useSalesEnablementContext from '../../../../../../gqlCommon/hooks/useSalesEnablementContext';
import { ComplexAccordionRef } from '../../../../../../gqlCommon/hooks/useRefs';

export interface IFeatureCategoryProps {
  category: ICategory;
  id: number;
  expandedPanelId: string;
  setExpanded: (expanded: string) => void;
  openAccessoryDetailsModal: (value: boolean, feature: IFeature) => void;
}

const FeatureCategory: FunctionComponent<IFeatureCategoryProps> = ({
  category,
  id,
  expandedPanelId,
  setExpanded,
  openAccessoryDetailsModal,
}) => {
  const styles = featureCategoryStyles();
  const { isInSalesEnablement, registerFeatureCategoryRef } = useSalesEnablementContext();
  const categoryFeaturesList = category?.featuresList;
  const subcategories = category?.subcategories;
  const featuresTotalCount = availableFeaturesService.getFeaturesTotalCount(category?.subcategories);

  const exposeRef = useCallback(
    (ref: ComplexAccordionRef) => {
      registerFeatureCategoryRef(`panel_${id}`, ref);
    },
    [id, registerFeatureCategoryRef],
  );

  const displayedFeatures = useMemo(
    () =>
      !isInSalesEnablement
        ? categoryFeaturesList.features.filter(item => !item.attributes.salespersonViewOnly)
        : categoryFeaturesList.features,
    [categoryFeaturesList.features, isInSalesEnablement],
  );

  const count = subcategories ? featuresTotalCount : displayedFeatures.length;

  if (displayedFeatures.length === 0) {
    return null;
  }

  return (
    <ComplexAccordion
      id={id}
      expandedPanelId={expandedPanelId}
      setExpanded={setExpanded}
      exposeRef={exposeRef}
      summary={
        <FeatureCategoryTitle
          name={category.name}
          count={category?.attributes?.displaycount ? count : null}
          classes={{ title: styles.sectionTitle }}
          disclaimerIndicative={category?.attributes?.disclaimerindicative}
        />
      }
      details={
        <Grid style={{ width: '100%' }} data-test={`options:category:${category.name}`}>
          {subcategories ? (
            subcategories?.map((subcategory, index) => (
              <FeatureCategoryLayoutDisplay
                key={index}
                featuresList={subcategory.featuresList}
                categoryAttributes={category.attributes}
                categoryName={subcategory.name}
                openAccessoryDetailsModal={openAccessoryDetailsModal}
              />
            ))
          ) : (
            <FeatureCategoryLayoutDisplay
              featuresList={{
                features: displayedFeatures,
              }}
              categoryAttributes={category.attributes}
              openAccessoryDetailsModal={openAccessoryDetailsModal}
            />
          )}
        </Grid>
      }
      classes={{
        accordionSummary: styles.accordionSummary,
        accordionDetails: styles.accordionDetails,
        expandedIcon: styles.expandedIcon,
        expandIcon: styles.expandIcon,
        expandedSection: styles.expandedSectionTitle,
      }}
      dataTest={`accordion:category:${category.name}`}
    />
  );
};

export default FeatureCategory;
